import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RemoveIcon } from '../../assets/icons/remove-icon.svg';
import { ReactComponent as SentIcon } from '../../assets/icons/send-icon.svg';
import { ReactComponent as SuccessIcon } from '../../assets/icons/success-tick.svg';
import MyButton from '../button/Button';

interface ApprovalPropertes {
  selectedItems: string[];
  onSubmitReject: (description: string, items: string[]) => void;
  onSubmitApprove: (items: string[]) => void;
}

/**
 * Approval Action Bar Component
 *
 * Displays actions (Approve & Reject) when items are selected.
 * When the Reject button is clicked, a textarea appears requiring a reason.
 * Once the reason is provided and send icon clicked, it submits rejection.
 *
 * @component
 * @param {ApprovalPropertes} props - Component properties
 * @param {string[]} props.selectedItems - List of selected item IDs
 * @param {(status: 'approve' | 'reject', description: string, items: string[]) => void} props.onSubmit
 * Function triggered when either Approve or Reject is submitted
 *
 * @returns {JSX.Element} UI layout displaying Approval/Reject action bar
 */
function ApprovalActionBar({ selectedItems, onSubmitApprove, onSubmitReject }: ApprovalPropertes) {
  const { t } = useTranslation('penaltyManagement');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(false);
  const [showReason, setShowReason] = useState(false);

  /**
   * Handles rejection action.
   * - If reason box is hidden, show it
   * - If visible but empty, show error
   * - If valid reason given, submit rejection and reset fields
   */
  const handleReject = () => {
    if (!showReason) {
      setShowReason(true);
      return;
    }

    if (!description.trim()) {
      setError(true);
    }
  };

  /**
   * handle Reject Sent
   */
  const handleRejectSent = () => {
    if (!showReason) {
      setShowReason(true);
      return;
    }

    if (!description.trim()) {
      setError(true);
      return;
    }

    onSubmitReject(description, selectedItems);

    setDescription('');
    setError(false);
    setShowReason(false);
  };

  /**
   * Handles approval action.
   * - Clears textarea
   * - Hides reason box
   * - Calls submission handler
   */
  const handleApprove = () => {
    onSubmitApprove(selectedItems);
  };

  return (
    <div>
      {selectedItems.length > 0 && (
        <div className="approval-action-bar">
          {showReason && (
            <div className="reason-box-content">
              <p className="label">
                {t('reason')}
                <span className="mandatory">*</span>
              </p>

              <textarea
                className={`reason-box ${error ? 'error-border' : ''}`}
                placeholder={t('writeDescription')}
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setError(false);
                }}
              />

              {error && <p className="error-text">{t('reasonRequiredForRejection')}</p>}

              <div className="send-button">
                <MyButton
                  label=""
                  svgIcon={<SentIcon />}
                  buttonType="primary"
                  onClickFunc={handleRejectSent}
                />
              </div>
            </div>
          )}

          <div className="actions-row">
            <span className="item-count">
              {t('itemsSelected', { count: selectedItems.length })}
            </span>

            <div className="btn-group">
              <MyButton
                label={t('approve')}
                svgIcon={<SuccessIcon style={{ height: '20px', width: '20x' }} />}
                buttonType="none"
                onClickFunc={handleApprove}
                labelColorFill="#2E9E3A"
              />

              {showReason ? (
                <MyButton
                  label={t('reject')}
                  svgIcon={<RemoveIcon />}
                  buttonType="error"
                  onClickFunc={handleReject}
                />
              ) : (
                <MyButton
                  label={t('reject')}
                  svgIcon={<RemoveIcon />}
                  buttonType="none"
                  onClickFunc={handleReject}
                  labelColorFill="#F04438"
                  svgColorFill="#F04438"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApprovalActionBar;
